import { TaskConstants } from "../../constants/workflow/TaskConstants";
import { searchAclEntries } from "../../service/query/QueryService.full";
import { Status } from "../../service/Shared";

/**
 * The task's current visibility (its `readWorkflowTask` grants), read from the ACL — or `undefined`
 * when the read itself failed.
 *
 * `updateWorkflowTask` rewrites the field from what is sent, and the task search results carry no
 * visibility at all — so any caller updating a task it did not load through the panel has to read the
 * grants back first. Sending an empty list instead is rejected for a project workflow task, which
 * must keep its owner organisation visible (BR-02232), and would leave a general task unreadable,
 * since its grants are re-derived from the request (BR-02802).
 *
 * Which is why a failed read is reported rather than falling back to the owner organisation alone:
 * that list is indistinguishable from a task genuinely shared with nobody else, so an update built on
 * it would revoke every other organisation's access — quietly, and while reporting success. Callers
 * must abandon the update when this returns `undefined`.
 *
 * `ownerOrganisationUuid` is still added to a *successful* read that didn't include it, for BR-02232.
 */
export const getTaskVisibilityFromAcl = async (
  taskUuid: string,
  ownerOrganisationUuid: string | null | undefined
): Promise<string[] | undefined> => {
  const response = await searchAclEntries({ resourceUuid: taskUuid });

  if (response.status !== Status.Success || !response.data) return undefined;

  const subjectUuids = response.data
    .filter((entry) => entry.permissions.includes(TaskConstants.PERMISSION_READ_WORKFLOW_TASK))
    .map((entry) => entry.subjectUuid);

  if (ownerOrganisationUuid && !subjectUuids.includes(ownerOrganisationUuid)) {
    subjectUuids.push(ownerOrganisationUuid);
  }

  return subjectUuids;
};
